import { Container, Graphics } from 'pixi.js';
import { NEAR_MISS } from '@wooorms/shared';
import { TextPopupSystem } from './TextPopupSystem';
import { getSoundManager, SoundType } from '../../audio/SoundManager';

/**
 * NearMissSystem - Screen edge flash effect when player narrowly avoids collision
 *
 * Creates tension and excitement by providing visual feedback when
 * the player's head gets dangerously close to another worm.
 *
 * Features:
 * - Screen edge red flash for near misses
 * - Stronger flash + "CLOSE!" text for very close encounters
 * - Cooldown to prevent spam
 */
export class NearMissSystem {
  /** Container for screen overlay effects (should be added to UI layer, not world) */
  public container: Container;

  /** Screen flash overlay */
  private flashOverlay: Graphics;

  /** Current flash intensity (0-1) */
  private flashIntensity: number = 0;

  /** Flash decay timer */
  private flashTimer: number = 0;

  /** Cooldown timer to prevent spam */
  private cooldownTimer: number = 0;

  /** Reference to text popup system for "CLOSE!" text */
  private textPopupSystem: TextPopupSystem | null = null;

  /** Screen dimensions for overlay sizing */
  private screenWidth: number = 1920;
  private screenHeight: number = 1080;

  constructor() {
    this.container = new Container();
    this.container.label = 'near-miss-system';

    // Create flash overlay (screen edge glow effect)
    this.flashOverlay = new Graphics();
    this.flashOverlay.visible = false;
    this.container.addChild(this.flashOverlay);
  }

  /**
   * Set reference to text popup system for showing "CLOSE!" text
   */
  setTextPopupSystem(textPopupSystem: TextPopupSystem): void {
    this.textPopupSystem = textPopupSystem;
  }

  /**
   * Update screen dimensions for overlay sizing
   */
  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  /**
   * Check for near miss and trigger effect if applicable
   * @param playerX Player head X position
   * @param playerY Player head Y position
   * @param playerRadius Player head radius
   * @param otherWorms Array of other worms' segment data
   */
  checkNearMiss(
    playerX: number,
    playerY: number,
    playerRadius: number,
    otherWorms: Array<{
      segments: Array<{ x: number; y: number; radius: number }>;
      isAlive: boolean;
    }>
  ): void {
    // Skip if on cooldown
    if (this.cooldownTimer > 0) return;

    let closestDistance = Infinity;

    // Check distance to all other worms' segments
    for (const worm of otherWorms) {
      if (!worm.isAlive) continue;

      for (const segment of worm.segments) {
        const dx = playerX - segment.x;
        const dy = playerY - segment.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Calculate gap between edges (not centers)
        const combinedRadius = playerRadius + segment.radius;
        const gap = distance - combinedRadius;

        if (gap < closestDistance) {
          closestDistance = gap;
        }
      }
    }

    // Check for near miss (positive gap means not colliding)
    if (closestDistance > 0 && closestDistance <= NEAR_MISS.NEAR_THRESHOLD) {
      this.triggerNearMiss(closestDistance, playerX, playerY);
    }
  }

  /**
   * Trigger the near miss effect
   */
  private triggerNearMiss(distance: number, playerX: number, playerY: number): void {
    const isVeryClose = distance <= NEAR_MISS.VERY_CLOSE_THRESHOLD;

    // Set flash intensity based on how close
    this.flashIntensity = isVeryClose
      ? NEAR_MISS.FLASH_INTENSITY.VERY_CLOSE
      : NEAR_MISS.FLASH_INTENSITY.NEAR;

    this.flashTimer = NEAR_MISS.FLASH_DURATION_MS / 1000;
    this.cooldownTimer = NEAR_MISS.COOLDOWN_MS / 1000;

    // Draw the flash overlay
    this.drawFlashOverlay();

    // Play whoosh sound (louder for very close calls)
    const soundManager = getSoundManager();
    if (isVeryClose) {
      soundManager.play(SoundType.NEAR_MISS_CLOSE);
    } else {
      soundManager.play(SoundType.NEAR_MISS);
    }

    // Show "CLOSE!" text for very close encounters
    if (isVeryClose && this.textPopupSystem) {
      this.textPopupSystem.spawn({
        text: 'CLOSE!',
        x: playerX,
        y: playerY - 50, // Above player head
        color: 0xff3333,
        fontSize: 28,
        scale: 1.2,
        duration: 0.8,
        riseSpeed: 80,
      });
    }
  }

  /**
   * Draw the screen edge flash overlay
   */
  private drawFlashOverlay(): void {
    this.flashOverlay.clear();

    // Create a vignette-style edge glow
    // We draw a frame around the screen edges that fades inward
    const edgeWidth = Math.min(this.screenWidth, this.screenHeight) * 0.15;

    // Red warning color
    const color = 0xff0000;

    // Draw gradient rectangles on each edge
    // Top edge
    for (let i = 0; i < 10; i++) {
      const alpha = this.flashIntensity * (1 - i / 10) * 0.5;
      const y = i * (edgeWidth / 10);
      this.flashOverlay.rect(0, y, this.screenWidth, edgeWidth / 10);
      this.flashOverlay.fill({ color, alpha });
    }

    // Bottom edge
    for (let i = 0; i < 10; i++) {
      const alpha = this.flashIntensity * (1 - i / 10) * 0.5;
      const y = this.screenHeight - edgeWidth + i * (edgeWidth / 10);
      this.flashOverlay.rect(0, y, this.screenWidth, edgeWidth / 10);
      this.flashOverlay.fill({ color, alpha });
    }

    // Left edge
    for (let i = 0; i < 10; i++) {
      const alpha = this.flashIntensity * (1 - i / 10) * 0.5;
      const x = i * (edgeWidth / 10);
      this.flashOverlay.rect(x, 0, edgeWidth / 10, this.screenHeight);
      this.flashOverlay.fill({ color, alpha });
    }

    // Right edge
    for (let i = 0; i < 10; i++) {
      const alpha = this.flashIntensity * (1 - i / 10) * 0.5;
      const x = this.screenWidth - edgeWidth + i * (edgeWidth / 10);
      this.flashOverlay.rect(x, 0, edgeWidth / 10, this.screenHeight);
      this.flashOverlay.fill({ color, alpha });
    }

    this.flashOverlay.visible = true;
  }

  /**
   * Update the system
   * @param dt Delta time in seconds
   */
  update(dt: number): void {
    // Update cooldown
    if (this.cooldownTimer > 0) {
      this.cooldownTimer -= dt;
    }

    // Update flash
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;

      // Fade out flash
      const progress = this.flashTimer / (NEAR_MISS.FLASH_DURATION_MS / 1000);
      this.flashOverlay.alpha = progress;

      if (this.flashTimer <= 0) {
        this.flashOverlay.visible = false;
        this.flashIntensity = 0;
      }
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.flashOverlay.destroy();
    this.container.destroy({ children: true });
  }
}
